import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DonutSegment, PdfSummaryCard, PdfSummaryData, PdfTotalCard } from '../../types/registryPdf';
import { CONTENT_HEIGHT, PDF_PAGE_HEIGHT, PDF_PAGE_WIDTH, isFullWidthCard } from '../../utils/registryPdfUtils';
import './RegistryPdfSummary.css';

const SUMMARY_ROW_GAP = 16;
const EMPTY_DONUT_COLOR = '#e5e8ec';
const DONUT_PIXEL_RATIO = 4;
const TOTAL_DONUT_SIZE = 100;
const TOTAL_DONUT_THICKNESS = 19;


const formatPercent = (value: number, total: number): string => {
    const percent = total > 0 ? (value / total) * 100 : 0;
    return `${percent.toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
};


// Чтобы четырёхзначное число не залезало на кольцо
const getValueFontSize = (value: number, innerDiameter: number, maxSize: number): number => {
    return Math.min(maxSize, Math.floor((innerDiameter * 0.85) / (String(value).length * 0.62)));
};


const getSegmentsTotal = (segments: DonutSegment[]): number => {
    return segments.reduce((sum, segment) => sum + segment.value, 0);
};


interface DonutProps {
    segments: DonutSegment[];
    size: number;
    thickness: number;
    children?: React.ReactNode;
}

// Рисуем на canvas, а не через SVG, SVG-дуги html2canvas теряет
const Donut: React.FC<DonutProps> = ({ segments, size, thickness, children }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useLayoutEffect(() => {
        const context = canvasRef.current ? canvasRef.current.getContext('2d') : null;
        if (!context) return;
        context.setTransform(DONUT_PIXEL_RATIO, 0, 0, DONUT_PIXEL_RATIO, 0, 0);
        context.clearRect(0, 0, size, size);
        context.lineWidth = thickness;

        const radius = (size - thickness) / 2;
        const center = size / 2;
        const total = getSegmentsTotal(segments);
        if (total === 0) {
            context.strokeStyle = EMPTY_DONUT_COLOR;
            context.beginPath();
            context.arc(center, center, radius, 0, Math.PI * 2);
            context.stroke();
            return;
        }

        const filledSegments = segments.filter(segment => segment.value > 0);
        const gap = filledSegments.length > 1 ? 0.04 : 0;
        let startAngle = -Math.PI / 2;
        filledSegments.forEach(segment => {
            const segmentAngle = (segment.value / total) * Math.PI * 2;
            const visibleAngle = Math.max(segmentAngle - gap, 0.01);
            const padding = (segmentAngle - visibleAngle) / 2;
            context.strokeStyle = segment.color;
            context.beginPath();
            context.arc(center, center, radius, startAngle + padding, startAngle + padding + visibleAngle);
            context.stroke();
            startAngle += segmentAngle;
        });
    }, [segments, size, thickness]);

    return (
        <div className="registry-pdf-donut" style={{ width: size, height: size }}>
            <canvas ref={canvasRef} width={size * DONUT_PIXEL_RATIO} height={size * DONUT_PIXEL_RATIO} style={{ width: size, height: size }} />
            <div className="registry-pdf-donut__center">{children}</div>
        </div>
    );
};


interface YearDonutsProps {
    years: string[];
    segmentsByYear: { [year: string]: DonutSegment[] };
    isWide: boolean;
}

const YearDonuts: React.FC<YearDonutsProps> = ({ years, segmentsByYear, isWide }) => {
    const donutSize = years.length > 5 ? 42 : 48;
    const thickness = donutSize > 45 ? 8 : 7;
    // Ряды делаем ровными: 9 лет = 5 + 4, а не 6 + 3
    const maxPerRow = isWide ? 12 : 6;
    const rowsCount = Math.ceil(years.length / maxPerRow) || 1;
    const donutsPerRow = Math.ceil(years.length / rowsCount) || 1;

    return (
        <div className="registry-pdf-years" style={{ gridTemplateColumns: `repeat(${donutsPerRow}, 1fr)` }}>
            {years.map(year => {
                const segments = segmentsByYear[year] || [];
                const yearTotal = getSegmentsTotal(segments);
                return (
                    <div key={year} className="registry-pdf-years__item">
                        <Donut segments={segments} size={donutSize} thickness={thickness}>
                            <span className="registry-pdf-years__value" style={{ fontSize: getValueFontSize(yearTotal, donutSize - thickness * 2, 11.5) }}>
                                {yearTotal}
                            </span>
                        </Donut>
                        <span className="registry-pdf-years__label">{year}</span>
                    </div>
                );
            })}
        </div>
    );
};


interface ReportHeaderProps {
    userName: string;
    generatedAt: Date;
}

const ReportHeader: React.FC<ReportHeaderProps> = ({ userName, generatedAt }) => (
    <div className="registry-pdf-header">
        <div>
            <div className="registry-pdf-header__brand">АПТС</div>
            <div className="registry-pdf-header__caption">Ассоциация производителей трубопроводных систем</div>
        </div>
        <div className="registry-pdf-header__meta">
            <div>
                Сформировано{' '}
                {generatedAt.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </div>
        </div>
    </div>
);


const TotalCardContent: React.FC<{ card: PdfTotalCard; isWide: boolean }> = ({ card, isWide }) => {
    const total = getSegmentsTotal(card.segments);
    return (
        <>
            <div className="registry-pdf-total">
                <Donut segments={card.segments} size={TOTAL_DONUT_SIZE} thickness={TOTAL_DONUT_THICKNESS}>
                    <span className="registry-pdf-total__value" style={{ fontSize: getValueFontSize(total, TOTAL_DONUT_SIZE - TOTAL_DONUT_THICKNESS * 2, 24) }}>
                        {total}
                    </span>
                </Donut>
                <ul className="registry-pdf-legend registry-pdf-legend--round">
                    {card.segments.map(segment => (
                        <li key={segment.label}>
                            <span className="registry-pdf-legend__marker" style={{ background: segment.color }} />
                            <span>
                                {segment.label} — <b>{segment.value}</b>{' '}
                                <span className="registry-pdf-legend__muted">({formatPercent(segment.value, total)})</span>
                            </span>
                        </li>
                    ))}
                </ul>
            </div>
            {card.segmentsByYear && card.years.length > 0 && (
                <>
                    <div className="registry-pdf-panel__subtitle">Динамика по годам</div>
                    <YearDonuts years={card.years} segmentsByYear={card.segmentsByYear} isWide={isWide} />
                </>
            )}
        </>
    );
};


// isWide — карточка без пары в ряду, растягивается на всю ширину
const SummaryCard: React.FC<{ card: PdfSummaryCard; isWide: boolean }> = ({ card, isWide }) => {
    let content: React.ReactNode = null;

    if (card.kind === 'total') {
        content = <TotalCardContent card={card} isWide={isWide} />;
    } else if (card.kind === 'dynamics') {
        content = (
            <>
                <YearDonuts years={card.years} segmentsByYear={card.segmentsByYear} isWide={isWide} />
                <ul className={isWide ? 'registry-pdf-legend registry-pdf-legend--columns' : 'registry-pdf-legend'}>
                    {card.legend.map(entry => (
                        <li key={entry.label}>
                            <span className="registry-pdf-legend__marker" style={{ background: entry.color }} />
                            <span>{entry.label} — <b>{entry.value}</b></span>
                        </li>
                    ))}
                </ul>
            </>
        );
    } else if (card.kind === 'table') {
        const totalWeight = card.columns.reduce((sum, column) => sum + (column.weight || 1), 0);
        content = (
            <>
                {card.subtitle && <div className="registry-pdf-panel__note">{card.subtitle}</div>}
                <table className="registry-pdf-table">
                    <colgroup>
                        {card.columns.map(column => (
                            <col key={column.title} style={{ width: `${((column.weight || 1) / totalWeight) * 100}%` }} />
                        ))}
                    </colgroup>
                    <thead>
                        <tr>{card.columns.map(column => <th key={column.title}>{column.title}</th>)}</tr>
                    </thead>
                    <tbody>
                        {card.rows.length === 0 && <tr><td colSpan={card.columns.length}>Нет данных</td></tr>}
                        {card.rows.map((row, rowIndex) => (
                            <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell || '—'}</td>)}</tr>
                        ))}
                    </tbody>
                </table>
            </>
        );
    } else if (card.kind === 'tiles') {
        content = (
            <div className="registry-pdf-tiles">
                {card.tiles.map(tile => (
                    <div key={tile.label} className="registry-pdf-tiles__item">
                        <span>{tile.label}</span>
                        <b>{tile.value}</b>
                    </div>
                ))}
            </div>
        );
    } else if (card.kind === 'image') {
        content = <img className="registry-pdf-image" src={card.src} alt="" style={{ aspectRatio: `${card.width} / ${card.height}` }} />;
    }

    return (
        <div className={isWide ? 'registry-pdf-panel registry-pdf-panel--wide' : 'registry-pdf-panel'}>
            <div className="registry-pdf-panel__title">{card.title}</div>
            {content}
        </div>
    );
};


// Итоги и динамика встают по две в ряд, таблицы, плитки и графики — по одной
const groupCardsIntoRows = (cards: PdfSummaryCard[]): PdfSummaryCard[][] => {
    const rows: PdfSummaryCard[][] = [];
    cards.forEach(card => {
        const lastRow = rows[rows.length - 1];
        if (lastRow && lastRow.length === 1 && !isFullWidthCard(lastRow[0]) && !isFullWidthCard(card)) {
            lastRow.push(card);
        } else {
            rows.push([card]);
        }
    });
    return rows;
};


// Раскладываем ряды по листам A4, не разрывая ряд
const splitRowsIntoPages = (rowHeights: number[], firstPageTop: number, nextPageTop: number): number[][] => {
    const pages: number[][] = [];
    let pageRows: number[] = [];
    let usedHeight = firstPageTop;
    rowHeights.forEach((rowHeight, rowIndex) => {
        if (pageRows.length > 0 && usedHeight + SUMMARY_ROW_GAP + rowHeight > CONTENT_HEIGHT) {
            pages.push(pageRows);
            pageRows = [];
            usedHeight = nextPageTop;
        }
        usedHeight += (pageRows.length > 0 ? SUMMARY_ROW_GAP : 0) + rowHeight;
        pageRows.push(rowIndex);
    });
    if (pageRows.length > 0 || pages.length === 0) pages.push(pageRows);
    return pages;
};


interface RegistryPdfSummaryProps {
    summary: PdfSummaryData;
    userName: string;
    generatedAt: Date;
    onReady: (summaryPages: HTMLElement[]) => void;
}

// Сводка отчёта рисуется за пределами экрана, сначала ряды карточек замеряются и раскладываются по листам,
// затем готовые листы отдаются на снимок в onReady
const RegistryPdfSummary: React.FC<RegistryPdfSummaryProps> = ({ summary, userName, generatedAt, onReady }) => {
    const measureRef = useRef<HTMLDivElement>(null);
    const pagesRef = useRef<HTMLDivElement>(null);
    const isReadyReportedRef = useRef(false);
    const [rowsByPage, setRowsByPage] = useState<number[][] | null>(null);

    const cardRows = groupCardsIntoRows(summary.cards);
    const [mainCounter, ...extraCounters] = summary.counters;

    const renderRow = (row: PdfSummaryCard[]) => {
        return row.map(card => <SummaryCard key={card.title} card={card} isWide={row.length === 1} />);
    };

    const intro = (
        <>
            <ReportHeader userName={userName} generatedAt={generatedAt} />
            <div className="registry-pdf-intro">
                <div>
                    <div className="registry-pdf-intro__title">{summary.title}</div>
                    <div className="registry-pdf-intro__filter">{summary.subtitle}</div>
                </div>
                {mainCounter && (
                    <div className="registry-pdf-intro__total">
                        <span>{mainCounter.label}</span>
                        <b>{mainCounter.value}</b>
                    </div>
                )}
            </div>
            {extraCounters.length > 0 && (
                <div className="registry-pdf-counters" style={{ gridTemplateColumns: `repeat(${Math.max(extraCounters.length, 3)}, 1fr)` }}>
                    {extraCounters.map(counter => (
                        <div key={counter.label} className="registry-pdf-intro__total">
                            <span>{counter.label}</span>
                            <b>{counter.value}</b>
                        </div>
                    ))}
                </div>
            )}
        </>
    );

    useLayoutEffect(() => {
        if (rowsByPage || !measureRef.current) return;
        const measureBlock = measureRef.current;
        const getHeight = (selector: string) => {
            const block = measureBlock.querySelector(selector) as HTMLElement | null;
            return block ? block.offsetHeight : 0;
        };
        const rowHeights: number[] = [];
        measureBlock.querySelectorAll('[data-measure="summary-row"]').forEach(rowBlock => {
            rowHeights.push((rowBlock as HTMLElement).offsetHeight);
        });
        setRowsByPage(splitRowsIntoPages(
            rowHeights,
            getHeight('[data-measure="intro"]') + SUMMARY_ROW_GAP,
            getHeight('[data-measure="header"]') + SUMMARY_ROW_GAP,
        ));
    }, [rowsByPage]);

    useEffect(() => {
        // Эффект может перезапуститься, а сборку PDF нужно запустить один раз
        if (!rowsByPage || !pagesRef.current || isReadyReportedRef.current) return;
        let isCancelled = false;
        const pages = Array.from(pagesRef.current.querySelectorAll('.registry-pdf-page')) as HTMLElement[];
        document.fonts.ready.then(() => {
            if (isCancelled || isReadyReportedRef.current) return;
            isReadyReportedRef.current = true;
            onReady(pages);
        });
        return () => {
            isCancelled = true;
        };
    }, [rowsByPage, onReady]);

    return createPortal(
        <div className="registry-pdf-root" aria-hidden="true">
            {!rowsByPage && (
                <div ref={measureRef} className="registry-pdf-sheet-content" style={{ width: PDF_PAGE_WIDTH }}>
                    <div data-measure="intro">{intro}</div>
                    <div data-measure="header"><ReportHeader userName={userName} generatedAt={generatedAt} /></div>
                    {cardRows.map((row, rowIndex) => (
                        <div key={rowIndex} data-measure="summary-row" className="registry-pdf-row">{renderRow(row)}</div>
                    ))}
                </div>
            )}
            {rowsByPage && (
                <div ref={pagesRef}>
                    {rowsByPage.map((pageRows, pageIndex) => (
                        <div key={pageIndex} className="registry-pdf-page" style={{ width: PDF_PAGE_WIDTH, minHeight: PDF_PAGE_HEIGHT }}>
                            {pageIndex === 0 ? intro : <ReportHeader userName={userName} generatedAt={generatedAt} />}
                            <div className="registry-pdf-rows">
                                {pageRows.map(rowIndex => (
                                    <div key={rowIndex} className="registry-pdf-row">{renderRow(cardRows[rowIndex])}</div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>,
        document.body,
    );
};

export default RegistryPdfSummary;
